import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Package } from 'lucide-react';
import { Page } from '@shared/ui/containers/Page';
import { ListGroup } from '@shared/ui/lists/ListGroup';
import { ListItem } from '@shared/ui/lists/ListItem';
import { Skeleton } from '@shared/ui/feedback/Skeleton';
import { ErrorState } from '@shared/ui/feedback/ErrorState';
import { EmptyState } from '@shared/ui/feedback/EmptyState';
import { formatMoney, formatRelative } from '@shared/lib/format';
import { fetchOrders, type OrderSummary } from './api';

const STATUS_TINT: Record<OrderSummary['status'], string> = {
  pending: 'bg-warning/10 text-warning',
  paid: 'bg-info/10 text-info',
  fulfilled: 'bg-success/10 text-success',
  cancelled: 'bg-danger/10 text-danger',
  refunded: 'bg-ink-muted/10 text-ink-muted',
};

export function Orders(): JSX.Element {
  const navigate = useNavigate();
  const ordersQuery = useQuery({ queryKey: ['orders'], queryFn: fetchOrders });

  if (ordersQuery.isLoading) return <Page><div className="flex flex-col gap-3"><Skeleton className="h-16" /><Skeleton className="h-16" /></div></Page>;
  if (ordersQuery.isError) return <Page><ErrorState onRetry={() => ordersQuery.refetch()} /></Page>;
  const orders = ordersQuery.data ?? [];
  if (orders.length === 0) {
    return <Page><EmptyState icon={<Package className="h-7 w-7" strokeWidth={1.5} />} title="No orders yet." description="Anything you order through the store appears here." /></Page>;
  }

  return (
    <Page>
      <ListGroup>
        {orders.map((order) => (
          <ListItem
            key={order.order_id}
            title={`${formatMoney(order.total, { currency: order.currency })} · ${order.item_count} item${order.item_count === 1 ? '' : 's'}`}
            subtitle={`${order.reference} · ${formatRelative(order.placed_at)}`}
            trailing={<span className={`eyebrow rounded-pill px-2 py-1 ${STATUS_TINT[order.status]}`}>{order.status}</span>}
            onClick={() => navigate(`/app/orders/${order.order_id}`)}
          />
        ))}
      </ListGroup>
    </Page>
  );
}
